import { useEffect, useState, useRef, useCallback } from 'react';

/* eslint-disable react-hooks/exhaustive-deps */
const useMountEffect = callback => useEffect(callback, []);

const useIsMounted = () => {
  const [isMounted, setIsMounted] = useState(false);
  useMountEffect(() => {
    setIsMounted(true);
  });
  return isMounted;
};

/* istanbul ignore file */

// This is useful when you want to fire an effect only after you've mounted.
// Useful for example if you have memoized objects you want to export from
// custom hooks but only when they change after the initial construction
const useUpdateEffect = (effect, deps) => {
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) {
      return effect();
    }
    mounted.current = true;
  }, deps);
};

// https://www.joshwcomeau.com/snippets/react-hooks/use-timeout/
function useTimeout(callback, delay) {
  const timeoutRef = useRef(null);
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    const tick = () => savedCallback.current();
    if (typeof delay === 'number') {
      timeoutRef.current = window.setTimeout(tick, delay);
      return () => window.clearTimeout(timeoutRef.current);
    }
  }, [delay]);
  return timeoutRef;
}

const usePrevious = (value, defaultValue = null) => {
  const ref = useRef(defaultValue);
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

function useInterval(callback, interval) {
  const intervalRef = useRef(null);
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    const handler = (...args) => savedCallback.current(...args);
    if (interval) {
      intervalRef.current = setInterval(handler, interval);
      return () => clearInterval(intervalRef.current);
    }
  }, [interval]);
  return intervalRef;
}

function useOnce(initializer) {
  const [value] = useState(initializer);
  return value;
}

/**
 * A hook for navigating through paginated items (rows, cards, etc.) with automatic page transitions
 *
 * @param config - Configuration options for row navigation
 * @returns Navigation state and methods
 */
const useRowNavigation = ({
  // @ts-ignore not using pageSize currently, but keeping it for future use
  pageSize,
  // eslint-disable-line
  currentPage,
  totalPages,
  onPageChange,
  itemCount,
  onItemSelect,
  initialItemIndex = 0,
  isLoading = false
}) => {
  // Track navigation lock state with ref for immediate blocking
  const navigationLockedRef = useRef(false);
  // Initialize active item index
  const [activeItemIndex, setActiveItemIndex] = useState(initialItemIndex);
  // Store direction of navigation to set proper item index on page change
  const navigationDirectionRef = useRef(null);
  // When page changes, set appropriate item index
  useEffect(() => {
    if (itemCount <= 0) {
      setActiveItemIndex(0);
      navigationDirectionRef.current = null;
      return;
    }
    if (navigationDirectionRef.current === 'backward') {
      // When going backward, select last item of new page
      setActiveItemIndex(itemCount - 1);
    } else {
      // When going forward or initial load, select first item
      setActiveItemIndex(0);
    }
    // Reset direction after use
    navigationDirectionRef.current = null;
  }, [currentPage, itemCount]);
  // Unlock navigation when loading completes
  useEffect(() => {
    if (!isLoading && navigationLockedRef.current) {
      navigationLockedRef.current = false;
    }
  }, [isLoading]);
  // Update active item and trigger callbacks
  const updateActiveItem = useCallback(newIndex => {
    setActiveItemIndex(newIndex);
    if (onItemSelect) {
      onItemSelect(newIndex);
    }
  }, [onItemSelect]);
  // Navigate to next item
  const gotoNextItem = useCallback(() => {
    // Immediate block - either we locked it OR external loading
    if (navigationLockedRef.current || isLoading) {
      return;
    }
    // If we're at the last item on the current page
    if (activeItemIndex === itemCount - 1) {
      // If we're not on the last page, go to next page
      if (currentPage < totalPages) {
        const nextPage = currentPage + 1;
        // IMMEDIATELY lock navigation (synchronous ref update)
        navigationLockedRef.current = true;
        // Set navigation direction
        navigationDirectionRef.current = 'forward';
        onPageChange(nextPage);
        // The effect will handle setting activeItemIndex when page changes
      }
      // Otherwise, we're at the last item of the last page - do nothing
    } else {
      // Just go to next item on current page
      updateActiveItem(activeItemIndex + 1);
    }
  }, [isLoading, activeItemIndex, itemCount, currentPage, totalPages, onPageChange, updateActiveItem]);
  // Navigate to previous item
  const gotoPreviousItem = useCallback(() => {
    // Immediate block - either we locked it OR external loading
    if (navigationLockedRef.current || isLoading) {
      return;
    }
    // If we're at the first item on the current page
    if (activeItemIndex === 0) {
      // If we're not on the first page, go to previous page
      if (currentPage > 1) {
        const prevPage = currentPage - 1;
        // IMMEDIATELY lock navigation (synchronous ref update)
        navigationLockedRef.current = true;
        // Set navigation direction
        navigationDirectionRef.current = 'backward';
        onPageChange(prevPage);
        // The effect will handle setting activeItemIndex when page changes
      }
      // Otherwise, we're at the first item of the first page - do nothing
    } else {
      // Just go to previous item on current page
      updateActiveItem(activeItemIndex - 1);
    }
  }, [isLoading, activeItemIndex, currentPage, onPageChange, updateActiveItem]);
  // Allow external control of active item
  const setActiveItemIndexExternal = useCallback(index => {
    if (index >= 0 && index < itemCount) {
      updateActiveItem(index);
    }
  }, [itemCount, updateActiveItem]);
  return {
    activeItemIndex,
    setActiveItemIndex: setActiveItemIndexExternal,
    gotoNextItem,
    gotoPreviousItem,
    isFirstItem: itemCount > 0 && activeItemIndex === 0 && currentPage === 1,
    isLastItem: itemCount > 0 && activeItemIndex === itemCount - 1 && currentPage === totalPages
  };
};

export { useInterval, useIsMounted, useMountEffect, useOnce, usePrevious, useRowNavigation, useTimeout, useUpdateEffect };
//# sourceMappingURL=hooks.js.map
